"use client";

import React from "react";
import { Box } from "@mui/material";

export interface CanvasObjectProps {
  src: string;
  alt: string;
  /** Width over height of the piece. */
  aspect: number;
  /** Rendered width in px. Height follows from the aspect. */
  width?: number | string;
  /** Depth of the wrapped edge in px. Scaled down for small tiles. */
  depth?: number;
  /**
   * The part of `src` shown on the front, as fractions of the image. Omitted,
   * the whole image is shown (cropped to fit by `object-fit: cover`). The
   * canvas editor passes the customer's own framing, so the room preview
   * shows exactly what they framed rather than the middle of the photo.
   */
  crop?: { x: number; y: number; width: number; height: number };
}

/**
 * A canvas as an object rather than a thumbnail.
 *
 * The right-hand sliver is the photo turning the corner onto the stretcher,
 * and the piece casts a real shadow. It is the same visual language as the
 * editor's CanvasMockup, and it is what tells a customer at a glance that they
 * are browsing physical things and not a picture library.
 */
export default function CanvasObject({
  src,
  alt,
  aspect,
  width = "100%",
  depth = 6,
  crop,
}: CanvasObjectProps) {
  const cropped = crop && crop.width > 0 && crop.height > 0;
  return (
    <Box
      sx={{
        position: "relative",
        width,
        aspectRatio: String(aspect || 1),
        boxShadow: "0 18px 34px -14px rgba(20,24,32,.45)",
        flexShrink: 0,
      }}
    >
      {crop && cropped ? (
        <Box sx={{ position: "absolute", inset: 0, overflow: "hidden" }}>
          <Box
            component="img"
            src={src}
            alt={alt}
            sx={{
              position: "absolute",
              maxWidth: "none",
              width: `${100 / crop.width}%`,
              height: `${100 / crop.height}%`,
              left: `${(-crop.x / crop.width) * 100}%`,
              top: `${(-crop.y / crop.height) * 100}%`,
              display: "block",
            }}
          />
        </Box>
      ) : (
        <Box
          component="img"
          src={src}
          alt={alt}
          loading="lazy"
          sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      )}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: 0,
          right: -depth,
          width: depth,
          height: "100%",
          background:
            "linear-gradient(90deg, rgba(0,0,0,.22), rgba(0,0,0,.42))",
          transform: "skewY(-7deg)",
          transformOrigin: "left top",
        }}
      />
    </Box>
  );
}
